/**
 * Shared visual shell for all unauthenticated auth screens — signup, login,
 * forgot-password, reset-password, verify-email.
 *
 * Aesthetic (global design system):
 *   - #0A0A0A base / #121212 cards
 *   - Teal (#00D4FF) velocity accent + warm pink (#FF4D94) CTAs
 *   - Left: ambient wave rings motion • Right: form card with glow
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Waves } from 'lucide-react';

export default function AuthShell({ title, subtitle, children, footer, testId }) {
  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white" data-testid={testId}>
      {/* LEFT — brand side */}
      <div className="hidden lg:flex w-[48%] relative overflow-hidden items-center justify-center p-16"
           style={{ background: 'radial-gradient(ellipse at 30% 20%, rgba(0,212,255,0.14), transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(255,77,148,0.12), transparent 60%), #0A0A0A' }}>
        <div className="absolute inset-0 pointer-events-none">
          {[0, 1, 2, 3].map(i => (
            <div
              key={`ring-${i}`}
              className="absolute rounded-full border border-[#00D4FF]/10 animate-wave-ripple"
              style={{
                width: `${220 + i * 80}px`,
                height: `${220 + i * 80}px`,
                top: '50%',
                left: '50%',
                marginLeft: `-${110 + i * 40}px`,
                marginTop: `-${110 + i * 40}px`,
                animationDelay: `${i * 0.8}s`,
              }}
            />
          ))}
        </div>
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-[#00D4FF]/15 flex items-center justify-center" style={{ boxShadow: '0 0 24px rgba(0,212,255,0.35)' }}>
              <Waves className="h-6 w-6 text-[#00D4FF]" />
            </div>
            <span className="text-2xl font-black tracking-tight">WAVE</span>
            <span className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">Brand&nbsp;DSP</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-black leading-[1.05] mb-4">
            Redefining the Future of<br /><span className="text-[#00D4FF]">Influencer Commerce</span>.
          </h1>
          <p className="text-sm text-gray-400 leading-relaxed mb-8">
            The ultimate demand-side platform that turns everyday creators into a
            synchronized, high-impact media channel.
          </p>
          <ul className="space-y-3 text-xs text-gray-300">
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] mt-1.5 flex-shrink-0" />
              <span>
                <strong className="text-white">Wave Technology™</strong>
                <span className="text-gray-500"> (patent pending)</span> — Temporal
                Synchronization Engine
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D94] mt-1.5 flex-shrink-0" />
              <span>
                <strong className="text-white">Live Performance Command Center</strong> —
                adaptive insights &amp; reporting in real time
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] mt-1.5 flex-shrink-0" />
              <span>
                <strong className="text-white">10-stage orchestration engine</strong> —
                every Wave cascades with Patient-Zero, Bursts, Reply Storms &amp; live reaccelerate
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* RIGHT — form side */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <Waves className="h-6 w-6 text-[#00D4FF]" />
            <span className="text-xl font-black">WAVE</span>
          </div>
          <h2 className="text-3xl font-black mb-2" data-testid="auth-title">{title}</h2>
          {subtitle && <p className="text-sm text-gray-400 mb-7">{subtitle}</p>}
          <div className="rounded-2xl p-7 border bg-[#121212] border-[#1f1f1f]" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
            {children}
          </div>
          {footer && <div className="text-center text-xs text-gray-500 mt-6">{footer}</div>}
          <div className="text-center text-[10px] text-gray-700 mt-6 space-x-3" data-testid="auth-legal-links">
            <Link to="/privacy" className="hover:text-gray-400 transition" data-testid="auth-link-privacy">Privacy</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-gray-400 transition" data-testid="auth-link-terms">Terms</Link>
            <span>·</span>
            <a href="mailto:legal@wavedsp.ai" className="hover:text-gray-400 transition">Contact</a>
          </div>
        </div>
      </div>
    </div>
  );
}
